import { Badge, Button, Flex, Separator, Text } from '@chakra-ui/react';
import FilterPillPopover from '@components/common/FilterPillPopover';
import { allSectors, Sector, SectorInfo } from '@features/supply/fakeSupplyChainData';
import { memo, useCallback, useMemo } from 'react';
import { LuCheck, LuX } from 'react-icons/lu';

interface SectorFilterProps {
  selectedSectors: Set<Sector>;
  onToggleSector: (sectorId: Sector) => void;
  onClearAll: () => void;
}

const SectorFilterItem = memo(
  ({
    sector,
    isSelected,
    onToggle,
  }: {
    sector: SectorInfo;
    isSelected: boolean;
    onToggle: (id: Sector) => void;
  }) => {
    const handleClick = useCallback(() => {
      onToggle(sector.id);
    }, [onToggle, sector.id]);

    return (
      <Flex
        alignItems="center"
        gap={2}
        px={3}
        py={1.5}
        borderRadius="md"
        cursor="pointer"
        _hover={{ bg: 'surface.hover' }}
        onClick={handleClick}
      >
        <Flex w={4} h={4} flexShrink={0} alignItems="center" justifyContent="center">
          {isSelected && <LuCheck size={14} />}
        </Flex>
        <Text fontWeight="medium" fontSize="sm">
          {sector.label}
        </Text>
      </Flex>
    );
  },
);
SectorFilterItem.displayName = 'SectorFilterItem';

const SectorFilter = memo(({ selectedSectors, onToggleSector, onClearAll }: SectorFilterProps) => {
  const hasFilters = selectedSectors.size > 0;

  const sectorButtonContent = useMemo(
    () => (
      <Flex alignItems="center" gap={1.5}>
        <Text>Sector</Text>
        {hasFilters && (
          <Badge
            variant="solid"
            colorPalette="gray"
            borderRadius="full"
            fontSize="2xs"
            px={1.5}
            minW="18px"
            textAlign="center"
          >
            {selectedSectors.size}
          </Badge>
        )}
      </Flex>
    ),
    [hasFilters, selectedSectors.size],
  );

  const selectedSectorItems = useMemo(
    () => allSectors.filter((s) => selectedSectors.has(s.id)),
    [selectedSectors],
  );

  return (
    <Flex direction="column" gap={3}>
      <Flex alignItems="center" gap={2}>
        <FilterPillPopover
          buttonContent={sectorButtonContent}
          isActive={hasFilters}
          isDefaultState={!hasFilters}
          buttonBorderRadius="full"
          positioning={{ placement: 'bottom-start' }}
        >
          <Flex direction="column" py={1} minW="200px">
            {allSectors.map((sector) => (
              <SectorFilterItem
                key={sector.id}
                sector={sector}
                isSelected={selectedSectors.has(sector.id)}
                onToggle={onToggleSector}
              />
            ))}
          </Flex>
        </FilterPillPopover>

        {hasFilters && (
          <>
            <Separator orientation="vertical" h="20px" />

            <Flex direction="row" gap={1} w="full" alignItems="center">
              <Text fontSize="xs" color="text.secondary">
                Filters:
              </Text>
              <Flex direction="row" gap={1} w="full" alignItems="center">
                {selectedSectorItems.map((sector) => (
                  <FilterTag
                    key={sector.id}
                    label={sector.label}
                    sectorId={sector.id}
                    onRemove={onToggleSector}
                  />
                ))}
              </Flex>

              <Button
                variant="ghost"
                size="xs"
                fontSize="xs"
                color="text.secondary"
                onClick={onClearAll}
              >
                <LuX size={12} />
                Clear all
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
});
SectorFilter.displayName = 'SectorFilter';

const FilterTag = memo(
  ({
    label,
    sectorId,
    onRemove,
  }: {
    label: string;
    sectorId: Sector;
    onRemove: (id: Sector) => void;
  }) => {
    const handleRemove = useCallback(() => {
      onRemove(sectorId);
    }, [onRemove, sectorId]);

    return (
      <Flex
        alignItems="center"
        gap={1}
        px={2}
        py={0.5}
        bg="surface.muted"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.light"
        fontSize="xs"
        cursor="pointer"
        _hover={{ bg: 'surface.hover' }}
        onClick={handleRemove}
      >
        <Text>{label}</Text>
        <LuX size={12} />
      </Flex>
    );
  },
);
FilterTag.displayName = 'FilterTag';

export default SectorFilter;
